import { InfiniteData, QueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { DEFAULT_COLOR_PALETTE } from '@/lib/constants/colors';
import { GroupBy, RateSourceResponse } from '@/modules/common/types';

import { getAssignmentsByDepartment } from '../api/assignments-by-department.api';
import { getAssignmentsByMember } from '../api/assignments-by-member.api';
import { getAssignmentsByProject } from '../api/assignments-by-project.api';
import usePlannerStore from '../store';
import {
  AssignmentProjectResponse,
  AssignmentResponse,
  AssignmentStatusResponse,
  DailyUtilizationResponse,
  PlannerAssignmentProjectResponse,
  PlannerAssignmentResponse,
  PlannerDepartmentResponse,
  PlannerMemberResponse,
  PlannerProjectResponse,
  PlannerUserStateChangeResponse,
} from '../types/dto';
import {
  AssignmentActivePeriodResponse,
  AssignmentRateResponse,
} from '../types/dto/response/planner-assignment-response';
import { PlannerTimeoffResponse } from '../types/dto/response/planner-timeoff-response';
import { UtilizationBasedOn } from '../types/utilization-based-on';
import { PlannerPreferences } from '../types/utilization-filters';
import {
  DualAxisPageParam,
  getContiguousRangeContaining,
  refreshGroupedQueryAsSingleRequest,
} from './planner-base/planner-pagination-helpers';
import {
  recalculateProjectUtilizationForAffectedDates,
  recalculateUtilizationForMember,
} from './recalculate-utilization';
import { getStateChangeForDate } from './state-change/state-change';

/**
 * Builds a stable fingerprint string for a single PlannerAssignmentResponse.
 *
 * Covers every field that can change server-side independently of syncToken:
 * - syncToken       — changes on any assignment field mutation
 * - isFilteredOut   — changes when planner filters are applied/removed
 * - belongsToTheGroup — changes when group filters change
 * - overlapsSameProject / overlapsAssignmentIds — changes when sibling assignments are mutated
 * - project — status changes when the project is (un)archived or (re)scheduled;
 *   client name / tentative probability change when the project itself is edited
 *
 * Add new PlannerAssignmentResponse fields here so select() memoization is
 * automatically invalidated whenever they change.
 */
export function buildAssignmentFingerprint(a: PlannerAssignmentResponse): string {
  // Per-date adjusted minutes change when an overlapping time-off is merged in
  // from another page; include them so select() memoization is invalidated and
  // the assignment re-renders shrunk around the time-off (otherwise it keeps a
  // stale full-height layout and visually overlaps the time-off block).
  const adjustedSignature = a.adjustedMinutesByDate
    ? Object.keys(a.adjustedMinutesByDate)
        .sort()
        .map(d => `${d}=${a.adjustedMinutesByDate![d]}`)
        .join('+')
    : '';
  const projectStatusSignature = [
    a.project.status ?? '',
    a.project.startsAt ?? '',
    a.project.nextArchiveDate ?? '',
    a.project.nextUnarchiveDate ?? '',
    // Colocated on the same node and rendered in the tooltip — without them a
    // refetch that only changes the client or probability keeps a stale tooltip.
    a.project.clientName ?? '',
    a.project.tentativeProbability ?? '',
  ].join('+');
  return [
    a.assignment.id,
    a.assignment.startDate,
    a.assignment.endDate,
    a.assignment.syncToken,
    a.isFilteredOut ? 1 : 0,
    a.belongsToTheGroup ? 1 : 0,
    a.overlapsSameProject ? 1 : 0,
    a.overlapsAssignmentIds.join('+'),
    a.bookedMinutes ?? '',
    adjustedSignature,
    projectStatusSignature,
  ].join(':');
}

/**
 * Builds a stable fingerprint for a single PlannerTimeoffResponse.
 *
 * MUST be included in the page fingerprints (member/department/project) so
 * select() memoization is invalidated whenever time-offs change. Without this,
 * a small time-off that lives entirely in one date range can disappear after
 * its page is pruned (scroll far away) and re-fetched (scroll back): the
 * re-fetched page can have an identical assignment/utilization signature, so a
 * fingerprint that ignores time-offs would not detect the restored time-off
 * and select() would return its stale memoized result.
 *
 * Captures the merged span and the per-day signature so cross-page day merges
 * (which extend days/startDate/endDate) change the fingerprint.
 */
export function buildTimeOffFingerprint(t: PlannerTimeoffResponse): string {
  const daySignature = t.days
    .map(d => `${d.date}|${d.portion}|${d.dayPartNumber ?? 'n'}|${d.minutes}`)
    .join(',');
  return [t.id, t.status, t.startDate, t.endDate, t.days.length, daySignature].join(':');
}

/**
 * Builds a fingerprint for a single PlannerUserStateChangeResponse.
 *
 * MUST be included in the member page fingerprint so select() memoization is
 * invalidated whenever a member's info changes. The member-info column reads all
 * these fields (capacity, rates, department/role/seniority, location, manager,
 * membership type, status) from the current state change — but editing e.g. a
 * member's location changes nothing in the assignment/time-off/utilization
 * signatures, so without this the refetched page produces an identical
 * fingerprint and select() returns its stale mapped result (the column keeps the
 * old value until reload).
 *
 * Add any new state-change field the sidebar renders here (see timeline-row-sidebar).
 */
export function buildStateChangeFingerprint(s: PlannerUserStateChangeResponse): string {
  return [
    s.effectiveStartDate,
    s.effectiveEndDate ?? '',
    s.displayStatus,
    s.onDemand ? 1 : 0,
    s.billableCapacityPercent,
    s.workingMinutesPerDay,
    s.department?.id ?? '',
    s.role?.id ?? '',
    s.seniority?.id ?? '',
    s.city ?? '',
    s.country ?? '',
    s.manager?.id ?? '',
    s.isManager ? 1 : 0,
    s.membershipType,
  ].join(':');
}

/**
 * Query keys for assignment data grouped by different views
 */
export const ASSIGNMENT_QUERY_KEYS = {
  byMember: ['assignments-by-member'] as const,
  byDepartment: ['assignments-by-department'] as const,
  byProject: ['assignments-by-project'] as const,
} as const;

/**
 * Builds the active TanStack Query key for a given query prefix using the current
 * store state (filters + utilization preferences).
 *
 * This mirrors the queryKey construction in use-assignments-base.ts so that
 * cache operations target only the currently active query, not stale query
 * variants left in the cache from previous filter selections.
 */
function buildActiveQueryKey(prefix: string): readonly unknown[] {
  const { filters, plannerPreferences } = usePlannerStore.getState();
  return [
    prefix,
    filters,
    {
      utilizationBasedOn: plannerPreferences.utilizationBasedOn,
      assignmentStatuses: plannerPreferences.assignmentStatuses,
      ignorePlaceholdersForGroup: plannerPreferences.ignorePlaceholdersForGroup,
    },
  ];
}

/**
 * Type for paginated data with date range metadata
 */
export interface PageWithDateRange {
  items: unknown[];
  _dateRange?: { startDate: string; endDate: string };
  [key: string]: unknown;
}

/**
 * Snapshot entry for a single query's data, preserving its full key for exact restoration.
 */
interface QuerySnapshot {
  queryKey: readonly unknown[];
  data: InfiniteData<PageWithDateRange>;
}

/**
 * Context returned by onMutate for optimistic updates rollback
 */
export interface OptimisticContext {
  /** Snapshots of all matching assignment queries for rollback */
  snapshots?: QuerySnapshot[];
  /** For create: temp negative ID to replace with real ID in onSuccess */
  tempAssignmentId?: number;
  /** For update/delete: captured assignment dates before mutation for utilization recalculation */
  oldAssignmentDates?: { startDate: string; endDate: string };
  /** For update/delete/cut: captured userId for utilization recalculation */
  assignmentUserId?: number;
}

/**
 * Applies a member-level transform to all three assignment cache views (byMember, byDepartment, byProject).
 * Eliminates the repeated pattern of calling setQueriesData three times with near-identical callbacks.
 *
 * @param queryClient - TanStack Query client
 * @param transformMember - Function that transforms a single PlannerMemberResponse
 */
export function applyOptimisticMemberUpdate(
  queryClient: QueryClient,
  transformMember: (member: PlannerMemberResponse) => PlannerMemberResponse
): void {
  queryClient.setQueriesData<InfiniteData<PageWithDateRange>>(
    { queryKey: ASSIGNMENT_QUERY_KEYS.byMember },
    oldData => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map(page => ({
          ...page,
          items: (page.items as PlannerMemberResponse[]).map(transformMember),
        })),
      };
    }
  );

  queryClient.setQueriesData<InfiniteData<PageWithDateRange>>(
    { queryKey: ASSIGNMENT_QUERY_KEYS.byDepartment },
    oldData => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map(page => ({
          ...page,
          items: (page.items as PlannerDepartmentResponse[]).map(dept => ({
            ...dept,
            members: dept.members.map(transformMember),
          })),
        })),
      };
    }
  );

  queryClient.setQueriesData<InfiniteData<PageWithDateRange>>(
    { queryKey: ASSIGNMENT_QUERY_KEYS.byProject },
    oldData => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map(page => ({
          ...page,
          items: (page.items as PlannerProjectResponse[]).map(proj => ({
            ...proj,
            members: proj.members.map(transformMember),
          })),
        })),
      };
    }
  );
}

/**
 * Cancels all assignment queries to prevent race conditions during optimistic updates
 */
export async function cancelAssignmentQueries(queryClient: QueryClient): Promise<void> {
  await queryClient.cancelQueries({ queryKey: ASSIGNMENT_QUERY_KEYS.byMember });
  await queryClient.cancelQueries({ queryKey: ASSIGNMENT_QUERY_KEYS.byDepartment });
  await queryClient.cancelQueries({ queryKey: ASSIGNMENT_QUERY_KEYS.byProject });
}

/**
 * Invalidates all assignment queries, triggering a full refetch from the backend.
 * Used as a fallback when client-side cache patching is disabled via feature flag.
 */
export function invalidateAllAssignmentQueries(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: ASSIGNMENT_QUERY_KEYS.byMember });
  queryClient.invalidateQueries({ queryKey: ASSIGNMENT_QUERY_KEYS.byDepartment });
  queryClient.invalidateQueries({ queryKey: ASSIGNMENT_QUERY_KEYS.byProject });
}

/**
 * After a successful mutation, refreshes the active assignment query in the background
 * to reconcile the optimistic cache patch with server truth (e.g. activePeriods split by
 * rate change boundaries, which cannot be computed client-side).
 *
 * Rules per grouping tab:
 * - Active tab: fires one consolidated request via `refreshGroupedQueryAsSingleRequest`
 *   (avoids N requests for N loaded vertical pages).
 * - Inactive tab with cached data: marks the active-filter query stale so it reloads on
 *   next visit. Does NOT fetch — the tab is not visible and the request would be wasted.
 * - Never-visited tab (no cached data): nothing to do.
 *
 * Uses `buildActiveQueryKey` to target only the query matching the current filters,
 * ignoring stale query variants left in the cache from previous filter selections.
 *
 * All three hooks (by-member, by-department, by-project) are always mounted simultaneously
 * (only the `enabled` flag differs), so observer count cannot detect the active tab —
 * `groupBy` from the store is the authoritative source.
 *
 * This function is intentionally fire-and-forget (not awaited). The optimistic patch
 * already provides an immediate smooth UI update; the background refresh corrects it.
 */
export function refetchActiveAssignmentQueriesInBackground(
  queryClient: QueryClient,
  /**
   * When true, the ACTIVE tab is refreshed for only the contiguous run the
   * viewport is in (the 6–12 months actually in memory around `firstVisibleDate`)
   * instead of the full loaded envelope. After far scrolling the cache can hold a
   * disconnected far island + the viewport run, so the envelope spans a pruned gap
   * (e.g. 2+ years) — wasteful to refetch. The periodic background refresh sets
   * this; mutation callers leave it false (refresh the whole envelope, unchanged).
   */
  scopeToViewportRun = false
): void {
  const { groupBy, firstVisibleDate } = usePlannerStore.getState();

  const configs: Array<{
    prefix: string;
    isActive: boolean;
    apiFn: (params: never) => Promise<unknown>;
    getItemWeight?: (item: never) => number;
  }> = [
    {
      prefix: ASSIGNMENT_QUERY_KEYS.byMember[0],
      isActive: groupBy === GroupBy.USERS,
      apiFn: getAssignmentsByMember,
    },
    {
      prefix: ASSIGNMENT_QUERY_KEYS.byDepartment[0],
      isActive: groupBy === GroupBy.DEPARTMENTS,
      apiFn: getAssignmentsByDepartment,
      getItemWeight: (item: PlannerDepartmentResponse) => item.members?.length ?? 1,
    },
    {
      prefix: ASSIGNMENT_QUERY_KEYS.byProject[0],
      isActive: groupBy === GroupBy.PROJECTS,
      apiFn: getAssignmentsByProject,
      getItemWeight: (item: PlannerProjectResponse) => item.members?.length ?? 1,
    },
  ];

  for (const { prefix, isActive, apiFn, getItemWeight } of configs) {
    const activeQueryKey = buildActiveQueryKey(prefix);
    const cached =
      queryClient.getQueryData<InfiniteData<PageWithDateRange, DualAxisPageParam>>(activeQueryKey);

    if (cached == null) continue;

    if (isActive) {
      // Scope the refresh to the viewport's contiguous loaded run when requested,
      // so the background refresh doesn't refetch a multi-year envelope spanning a
      // pruned gap. Falls back to the full envelope if the run can't be resolved.
      const dateRangeOverride =
        scopeToViewportRun && firstVisibleDate
          ? (getContiguousRangeContaining(cached.pages, firstVisibleDate) ?? undefined)
          : undefined;

      void refreshGroupedQueryAsSingleRequest(
        queryClient,
        activeQueryKey,
        apiFn,
        getItemWeight,
        dateRangeOverride
      );
    } else {
      void queryClient.invalidateQueries({ queryKey: activeQueryKey, exact: true });
    }
  }
}

/**
 * Snapshots current assignment data for rollback on error.
 * Uses getQueriesData with prefix matching to capture all queries
 * regardless of additional key segments (filters, utilization settings).
 */
export function snapshotAssignmentData(queryClient: QueryClient): OptimisticContext {
  const snapshots: QuerySnapshot[] = [];

  const queryKeys = [
    ASSIGNMENT_QUERY_KEYS.byMember,
    ASSIGNMENT_QUERY_KEYS.byDepartment,
    ASSIGNMENT_QUERY_KEYS.byProject,
  ];

  for (const queryKey of queryKeys) {
    const queries = queryClient.getQueriesData<InfiniteData<PageWithDateRange>>({ queryKey });
    for (const [key, data] of queries) {
      if (data) {
        snapshots.push({ queryKey: key, data });
      }
    }
  }

  return { snapshots };
}

/**
 * Rolls back optimistic updates by restoring previous data snapshots.
 * Restores each query using its exact full key captured during snapshot.
 */
export function rollbackAssignmentData(queryClient: QueryClient, context: OptimisticContext): void {
  if (!context.snapshots) return;

  for (const snapshot of context.snapshots) {
    queryClient.setQueryData(snapshot.queryKey, snapshot.data);
  }
}

/**
 * Result of searching the by-member cache for a specific assignment.
 */
interface AssignmentSearchResult {
  /** Date range of the found assignment */
  dates: { startDate: string; endDate: string };
  /** User ID of the member who owns the assignment */
  userId: number;
}

/**
 * Searches the by-member cache to find an assignment's current dates and owning user.
 * Uses getQueriesData (prefix matching) because actual query keys include extra segments
 * (filters, utilizationBasedOn, utilizationFilters) beyond the prefix key.
 *
 * @returns The assignment's dates and userId, or undefined if not found in cache
 */
export function findAssignmentInCache(
  queryClient: QueryClient,
  assignmentId: number
): AssignmentSearchResult | undefined {
  const found = findPlannerAssignmentInCache(queryClient, assignmentId);
  if (!found) return undefined;

  return {
    dates: {
      startDate: found.plannerAssignment.assignment.startDate,
      endDate: found.plannerAssignment.assignment.endDate,
    },
    userId: found.userId,
  };
}

/**
 * Searches the by-member cache for the full planner assignment entry and its
 * owning user. Uses getQueriesData (prefix matching) — see findAssignmentInCache.
 */
export function findPlannerAssignmentInCache(
  queryClient: QueryClient,
  assignmentId: number
): { plannerAssignment: PlannerAssignmentResponse; userId: number } | undefined {
  const byMemberQueries = queryClient.getQueriesData<InfiniteData<PageWithDateRange>>({
    queryKey: ASSIGNMENT_QUERY_KEYS.byMember,
  });

  for (const [, queryData] of byMemberQueries) {
    if (!queryData) continue;
    for (const page of queryData.pages) {
      for (const member of page.items as PlannerMemberResponse[]) {
        const found = member.assignments.find(a => a.assignment.id === assignmentId);
        if (found) {
          return { plannerAssignment: found, userId: member.user.id };
        }
      }
    }
  }

  return undefined;
}

/**
 * Options for patching assignment cache after a successful mutation.
 */
interface PatchCacheOptions {
  /** Assignment ID to find and patch (e.g., temp negative ID for create) */
  assignmentId?: number;
  /** Server response data with real id and syncToken */
  serverData?: AssignmentResponse;
  /** User ID for utilization recalculation */
  userId?: number;
  /** Previous assignment dates for utilization recalculation (update/delete) */
  oldDates?: { startDate: string; endDate: string };
  /** New assignment dates for utilization recalculation (create/update) */
  newDates?: { startDate: string; endDate: string };
  /** Currency code from the request payload (CUSTOM rate only) — not returned by backend */
  customRateCurrencyCode?: string;
}

/**
 * Patches assignment data AND recalculates utilization in a single setQueriesData pass per query key.
 * Combines ID/syncToken patching with utilization recalculation to avoid intermediate render states
 * that can cause assignments to briefly disappear.
 *
 * @param queryClient - TanStack Query client
 * @param options - What to patch (assignment data, utilization, or both)
 */
export function patchCacheAfterMutation(
  queryClient: QueryClient,
  options: PatchCacheOptions
): void {
  const { assignmentId, serverData, userId, oldDates, newDates, customRateCurrencyCode } = options;

  // Build assignment patcher if needed
  const shouldPatchAssignment = assignmentId !== undefined && serverData !== undefined;
  const patchAssignment = shouldPatchAssignment
    ? (plannerAssignment: PlannerAssignmentResponse): PlannerAssignmentResponse => {
        if (plannerAssignment.assignment.id !== assignmentId) return plannerAssignment;

        // Reconstruct rate for activePeriods from server response + request payload fields.
        // Create/update endpoints return AssignmentResponse without activePeriods, so rate info
        // is not available in serverData. We rebuild it from customRate + customRateCurrencyCode
        // (passed from the request payload) so the tooltip shows rate immediately after mutation.
        const rateFromServer: AssignmentRateResponse | null =
          serverData.customRate != null && customRateCurrencyCode
            ? {
                rate: serverData.customRate,
                currency: { code: customRateCurrencyCode, displayName: customRateCurrencyCode },
                assignmentRateSource: serverData.rateSource,
                projectBoundRateSource: null,
              }
            : null;

        const patchedActivePeriods = rateFromServer
          ? plannerAssignment.activePeriods.map(period => ({ ...period, rate: rateFromServer }))
          : plannerAssignment.activePeriods;

        return {
          ...plannerAssignment,
          activePeriods: patchedActivePeriods,
          assignment: {
            ...plannerAssignment.assignment,
            ...serverData,
          },
        };
      }
    : null;

  // Build utilization patcher if needed
  const affectedDates = collectAffectedDates(oldDates, newDates);
  const shouldPatchUtilization = userId !== undefined && affectedDates.length > 0;

  let utilizationBasedOn: ReturnType<
    typeof usePlannerStore.getState
  >['plannerPreferences']['utilizationBasedOn'];
  let utilizationFilters: ReturnType<typeof usePlannerStore.getState>['plannerPreferences'];
  let isBillableMode = false;
  if (shouldPatchUtilization) {
    const state = usePlannerStore.getState();
    utilizationBasedOn = state.plannerPreferences.utilizationBasedOn;
    utilizationFilters = state.plannerPreferences;
    isBillableMode = utilizationBasedOn === UtilizationBasedOn.BillableCapacity;
  }

  // Combined member patcher — applies both assignment patch and utilization recalculation
  const patchMember = (member: PlannerMemberResponse): PlannerMemberResponse => {
    let updated = member;

    if (patchAssignment) {
      updated = { ...updated, assignments: updated.assignments.map(patchAssignment) };
    }

    if (shouldPatchUtilization && updated.user.id === userId) {
      const newUtilizationByDate = recalculateUtilizationForMember(
        updated,
        affectedDates,
        utilizationBasedOn!,
        utilizationFilters!
      );

      updated = { ...updated, utilizationByDate: newUtilizationByDate };
    }

    return updated;
  };

  // Single pass per query key — patch by-member
  queryClient.setQueriesData<InfiniteData<PageWithDateRange>>(
    { queryKey: ASSIGNMENT_QUERY_KEYS.byMember },
    oldData => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        pages: oldData.pages.map(page => ({
          ...page,
          items: (page.items as PlannerMemberResponse[]).map(patchMember),
        })),
      };
    }
  );

  // Single pass — patch by-department
  queryClient.setQueriesData<InfiniteData<PageWithDateRange>>(
    { queryKey: ASSIGNMENT_QUERY_KEYS.byDepartment },
    oldData => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map(page => ({
          ...page,
          items: (page.items as PlannerDepartmentResponse[]).map(dept => {
            const updatedMembers = dept.members.map(patchMember);
            const hasAffectedMember =
              shouldPatchUtilization && dept.members.some(m => m.user.id === userId);

            if (!hasAffectedMember && !patchAssignment) return dept;

            return {
              ...dept,
              members: updatedMembers,
              ...(hasAffectedMember && {
                utilizationByDate: aggregateGroupUtilization(
                  updatedMembers,
                  affectedDates,
                  dept.utilizationByDate,
                  isBillableMode,
                  utilizationFilters
                ),
              }),
            };
          }),
        })),
      };
    }
  );

  // Single pass — patch by-project
  queryClient.setQueriesData<InfiniteData<PageWithDateRange>>(
    { queryKey: ASSIGNMENT_QUERY_KEYS.byProject },
    oldData => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map(page => ({
          ...page,
          items: (page.items as PlannerProjectResponse[]).map(proj => {
            const updatedMembers = proj.members.map(patchMember);
            const hasAffectedMember =
              shouldPatchUtilization && proj.members.some(m => m.user.id === userId);

            if (!hasAffectedMember && !patchAssignment) return proj;

            return {
              ...proj,
              members: updatedMembers,
              ...(hasAffectedMember && {
                // Project utilization only counts assignments belonging to this project,
                // and includes a member's capacity only when they have at least one
                // qualifying assignment for this project on that day.
                utilizationByDate: recalculateProjectUtilizationForAffectedDates(
                  proj.project.id,
                  updatedMembers,
                  affectedDates,
                  proj.utilizationByDate,
                  utilizationBasedOn!,
                  utilizationFilters!
                ),
              }),
            };
          }),
        })),
      };
    }
  );
}

const DATE_FORMAT = 'YYYY-MM-DD';
const SATURDAY = 6;
const SUNDAY = 0;

/**
 * Computes active periods for an assignment that skips weekends.
 * Splits the date range into contiguous weekday-only fragments.
 * If doSkipWeekdays is false, returns a single period spanning the whole range.
 *
 * @param startDate - Assignment start date (ISO string)
 * @param endDate - Assignment end date (ISO string)
 * @param doSkipWeekdays - Whether weekends should be excluded
 * @param rate - Rate to assign to each computed period (e.g. from the original assignment for optimistic updates)
 * @returns Array of active period responses matching the backend format
 */
export function computeActivePeriods(
  startDate: string,
  endDate: string,
  doSkipWeekdays: boolean,
  rate: AssignmentRateResponse | null = null
): AssignmentActivePeriodResponse[] {
  if (!doSkipWeekdays) {
    return [{ dateRange: { startDate, endDate }, rate }];
  }

  const periods: AssignmentActivePeriodResponse[] = [];
  let current = dayjs(startDate).startOf('day');
  const end = dayjs(endDate).startOf('day');

  let periodStart: dayjs.Dayjs | null = null;

  while (!current.isAfter(end, 'day')) {
    const dayOfWeek = current.day();
    const isWeekend = dayOfWeek === SATURDAY || dayOfWeek === SUNDAY;

    if (!isWeekend) {
      if (!periodStart) {
        periodStart = current;
      }
    } else if (periodStart) {
      // Weekend hit — close the current period at the previous day
      periods.push({
        dateRange: {
          startDate: periodStart.format(DATE_FORMAT),
          endDate: current.subtract(1, 'day').format(DATE_FORMAT),
        },
        rate,
      });
      periodStart = null;
    }

    current = current.add(1, 'day');
  }

  // Close any remaining open period
  if (periodStart) {
    periods.push({
      dateRange: {
        startDate: periodStart.format(DATE_FORMAT),
        endDate: end.format(DATE_FORMAT),
      },
      rate,
    });
  }

  return periods;
}

/**
 * Collects all dates between start and end (inclusive) as ISO strings.
 */
function collectDatesInRange(startDate: string, endDate: string): string[] {
  const dates: string[] = [];
  let current = dayjs(startDate).startOf('day');
  const end = dayjs(endDate).startOf('day');

  while (!current.isAfter(end, 'day')) {
    dates.push(current.format(DATE_FORMAT));
    current = current.add(1, 'day');
  }

  return dates;
}

/**
 * Collects all unique affected dates from old and new date ranges.
 */
function collectAffectedDates(
  oldDates?: { startDate: string; endDate: string },
  newDates?: { startDate: string; endDate: string }
): string[] {
  const set = new Set<string>();

  if (oldDates) {
    collectDatesInRange(oldDates.startDate, oldDates.endDate).forEach(d => set.add(d));
  }
  if (newDates) {
    collectDatesInRange(newDates.startDate, newDates.endDate).forEach(d => set.add(d));
  }

  return Array.from(set);
}

/**
 * Parameters for building an optimistic AssignmentResponse.
 */
export interface BuildOptimisticAssignmentParams {
  startDate: string;
  endDate: string;
  status: AssignmentStatusResponse;
  doSkipWeekdays: boolean;
  isBillable: boolean;
  label: string;
  minutesPerDay: number;
  note: string;
  isBackgroundWork: boolean;
  probability?: number | null;
  rateSource: RateSourceResponse;
  customRate?: number;
  projectId: number;
  projectName?: string;
  projectColorHex?: string;
}

/**
 * Builds an optimistic `AssignmentResponse` with a temporary negative ID.
 * Shared by create and duplicate mutations to show a placeholder before the server responds.
 */
export function buildOptimisticAssignment(
  params: BuildOptimisticAssignmentParams
): AssignmentResponse {
  return {
    id: -Date.now(),
    startDate: params.startDate,
    endDate: params.endDate,
    status: params.status,
    doSkipWeekdays: params.doSkipWeekdays,
    isBillable: params.isBillable,
    label: params.label,
    minutesPerDay: params.minutesPerDay,
    note: params.note,
    isBackgroundWork: params.isBackgroundWork,
    probability: params.probability ?? null,
    rateSource: params.rateSource,
    customRate: params.customRate,
    project: {
      id: params.projectId,
      name: params.projectName ?? '',
      colorHex: params.projectColorHex
        ? params.projectColorHex.replace('#', '')
        : DEFAULT_COLOR_PALETTE.slate.replace('#', ''),
    } as AssignmentProjectResponse,
    syncToken: '',
  };
}

/**
 * Placeholder planner project node for optimistic cache entries: the project's
 * client / probability / status info is unknown at optimistic time, corrected by
 * the visible-range invalidation on mutation success.
 */
export const unknownPlannerProject = (projectId: number): PlannerAssignmentProjectResponse => ({
  id: projectId,
  clientName: null,
  tentativeProbability: null,
  status: null,
  startsAt: null,
  nextArchiveDate: null,
  nextUnarchiveDate: null,
});

/**
 * Wraps an `AssignmentResponse` in a `PlannerAssignmentResponse` with default
 * overlap/filter flags and computed active periods.
 * Shared by create and duplicate mutations for optimistic cache entries.
 *
 * @param plannerProject Pass the source entry's project node when the optimistic
 * assignment is built from an already-loaded assignment of the same project
 * (e.g. duplicate); defaults to the all-unknown placeholder for new projects.
 */
export function buildOptimisticPlannerAssignment(
  assignment: AssignmentResponse,
  plannerProject: PlannerAssignmentProjectResponse = unknownPlannerProject(assignment.project.id)
): PlannerAssignmentResponse {
  return {
    assignment,
    project: plannerProject,
    overlapsSameProject: false,
    overlapsAssignmentIds: [],
    activePeriods: computeActivePeriods(
      assignment.startDate,
      assignment.endDate,
      assignment.doSkipWeekdays
    ),
    // Unknown until the server reports it
    bookedMinutes: null,
    isFilteredOut: false,
    belongsToTheGroup: true,
  };
}

/**
 * Appends a new planner assignment to the target member row in all three cache views
 * (by-member, by-department, by-project).
 * Shared by create and duplicate mutations for their optimistic `onMutate` step.
 */
export function addOptimisticAssignmentToCache(
  queryClient: QueryClient,
  userId: number,
  plannerAssignment: PlannerAssignmentResponse
): void {
  const addToMember = (member: PlannerMemberResponse): PlannerMemberResponse => {
    if (member.user.id !== userId) return member;
    return { ...member, assignments: [...member.assignments, plannerAssignment] };
  };

  queryClient.setQueriesData<InfiniteData<PageWithDateRange>>(
    { queryKey: ASSIGNMENT_QUERY_KEYS.byMember },
    oldData => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map(page => ({
          ...page,
          items: (page.items as PlannerMemberResponse[]).map(addToMember),
        })),
      };
    }
  );

  queryClient.setQueriesData<InfiniteData<PageWithDateRange>>(
    { queryKey: ASSIGNMENT_QUERY_KEYS.byDepartment },
    oldData => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map(page => ({
          ...page,
          items: (page.items as PlannerDepartmentResponse[]).map(dept => ({
            ...dept,
            members: dept.members.map(addToMember),
          })),
        })),
      };
    }
  );

  queryClient.setQueriesData<InfiniteData<PageWithDateRange>>(
    { queryKey: ASSIGNMENT_QUERY_KEYS.byProject },
    oldData => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        pages: oldData.pages.map(page => ({
          ...page,
          items: (page.items as PlannerProjectResponse[]).map(proj => ({
            ...proj,
            members: proj.members.map(addToMember),
          })),
        })),
      };
    }
  );
}

/**
 * Re-aggregates department/project-level utilization from member utilizations for affected dates.
 * For non-affected dates, keeps the existing aggregated values.
 */
function aggregateGroupUtilization(
  members: PlannerMemberResponse[],
  affectedDates: string[],
  existingUtilization: DailyUtilizationResponse[],
  isBillableMode: boolean,
  utilizationFilters?: PlannerPreferences
): DailyUtilizationResponse[] {
  const utilMap = new Map<string, DailyUtilizationResponse>();

  // Start with existing values, normalizing keys to YYYY-MM-DD
  for (const entry of existingUtilization) {
    const key = dayjs(entry.date).format(DATE_FORMAT);
    utilMap.set(key, { ...entry, date: key });
  }

  // Recalculate only affected dates by summing across all members
  for (const date of affectedDates) {
    let totalAssigned = 0;
    let totalCapacity = 0;

    const ignorePlaceholders = utilizationFilters?.ignorePlaceholdersForGroup ?? false;

    for (const member of members) {
      // Member utilization dates are already normalized by recalculateUtilizationForMember
      const memberEntry = member.utilizationByDate.find(
        u => dayjs(u.date).format(DATE_FORMAT) === date
      );
      if (memberEntry && !memberEntry.onDemand) {
        if (ignorePlaceholders) {
          const stateChange = getStateChangeForDate(member.stateChanges, dayjs(date));
          if (stateChange?.displayStatus === 'PLACEHOLDER') {
            continue;
          }
        }

        totalAssigned += memberEntry.assigned;
        totalCapacity += memberEntry.capacity;
      }
    }

    const percent =
      totalCapacity > 0
        ? Math.round((totalAssigned / totalCapacity) * 100)
        : isBillableMode
          ? 100
          : 0;

    utilMap.set(date, { date, assigned: totalAssigned, capacity: totalCapacity, percent });
  }

  return Array.from(utilMap.values()).sort((a, b) => a.date.localeCompare(b.date));
}
